const BRAND_COLOR = '#66A573'
const BRAND_COLOR_DARK = '#4d8a5a'
const APP_NAME = 'Ask QUIEN'
const APP_NAME_HTML = `<span style="font-family:'SFMono-Regular',Consolas,'Liberation Mono',monospace;font-weight:700;color:${BRAND_COLOR};">Ask QUIEN</span>`
const LOGO_URL = 'https://quality-intelligence.co/brand/qi-mark.png'
const APP_URL = process.env.AUTH_URL ?? process.env.NEXTAUTH_URL ?? 'https://quality-intelligence.co'

const STATUS_LABELS: Record<string, string> = {
  AWAITING_EXPERT: 'Awaiting expert',
  EXPERT_REVIEWING: 'Reviewing',
  RESPONSE_DRAFTING: 'Drafting',
  RESPONSE_READY: 'Response ready',
  AWAITING_REPLY: 'Awaiting reply',
}

export type DigestTicket = {
  id: string
  referenceNumber: string
  title: string | null
  status: string
  createdAt: Date
  summary?: string | null
}

function formatAge(date: Date): string {
  const diffMs = Date.now() - date.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  if (diffHours < 1) return '<1h'
  if (diffHours < 24) return `${diffHours}h`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d`
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function buildExpertDigestEmail({
  tickets,
}: {
  tickets: DigestTicket[]
}): { subject: string; html: string; text: string } {
  const count = tickets.length
  const subject = `${count} ticket${count === 1 ? '' : 's'} need${count === 1 ? 's' : ''} your attention`
  const inboxUrl = `${APP_URL}/inbox`

  const tableRows = tickets.map((ticket) => {
    const age = formatAge(ticket.createdAt)
    const status = escapeHtml(STATUS_LABELS[ticket.status] ?? ticket.status)
    const title = escapeHtml(ticket.title ?? 'Untitled ticket')
    const ref = escapeHtml(ticket.referenceNumber)
    const rawSummary = ticket.summary ?? ''
    const summaryTruncated = rawSummary.length > 120
      ? escapeHtml(rawSummary.slice(0, 117)) + '…'
      : escapeHtml(rawSummary) || '—'
    const ticketUrl = `${APP_URL}/tickets/${ticket.id}`

    return `
              <tr style="border-bottom:1px solid #f4f4f5;">
                <td style="padding:10px 8px;font-size:11px;font-family:monospace;color:#71717a;white-space:nowrap;">
                  <a href="${ticketUrl}" style="color:${BRAND_COLOR};text-decoration:none;">${ref}</a>
                </td>
                <td style="padding:10px 8px;font-size:13px;color:#18181b;max-width:200px;">${title}</td>
                <td style="padding:10px 8px;font-size:12px;color:#52525b;white-space:nowrap;">${status}</td>
                <td style="padding:10px 8px;font-size:12px;color:#71717a;white-space:nowrap;">${age}</td>
                <td style="padding:10px 8px;font-size:12px;color:#52525b;max-width:240px;">${summaryTruncated}</td>
              </tr>`
  }).join('')

  const textRows = tickets.map((ticket) => {
    const age = formatAge(ticket.createdAt)
    const status = STATUS_LABELS[ticket.status] ?? ticket.status
    const title = ticket.title ?? 'Untitled ticket'
    const rawSummary = ticket.summary ?? ''
    const summaryLine = rawSummary.length > 100 ? rawSummary.slice(0, 97) + '...' : rawSummary
    return `• [${ticket.referenceNumber}] ${title} — ${status} — ${age}${summaryLine ? `\n  ${summaryLine}` : ''}`
  }).join('\n')

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${subject}</title>
</head>
<body style="margin:0;padding:0;background-color:#f4f4f5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f4f5;padding:40px 16px;">
    <tr>
      <td align="center">
        <table width="100%" cellpadding="0" cellspacing="0" style="max-width:640px;">

          <!-- Brand header -->
          <tr>
            <td align="center" style="padding-bottom:24px;">
              <img src="${LOGO_URL}" width="64" height="64" alt="${APP_NAME}" style="display:block;border-radius:12px;" />
              <p style="margin:12px 0 0;font-size:18px;">${APP_NAME_HTML}</p>
            </td>
          </tr>

          <!-- Card -->
          <tr>
            <td style="background-color:#ffffff;border-radius:12px;border:1px solid #e4e4e7;padding:40px 40px 32px;">
              <table width="100%" cellpadding="0" cellspacing="0">

                <!-- Heading -->
                <tr>
                  <td style="padding-bottom:8px;">
                    <p style="margin:0;font-size:22px;font-weight:700;color:#18181b;letter-spacing:-0.3px;">
                      ${count} ticket${count === 1 ? '' : 's'} need${count === 1 ? 's' : ''} your attention
                    </p>
                  </td>
                </tr>

                <!-- Sub-heading -->
                <tr>
                  <td style="padding-bottom:28px;border-bottom:1px solid #f4f4f5;">
                    <p style="margin:0;font-size:14px;color:#71717a;">
                      The following tickets are waiting for an expert response.
                    </p>
                  </td>
                </tr>

                <!-- Tickets table -->
                <tr>
                  <td style="padding-top:20px;padding-bottom:28px;">
                    <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #e4e4e7;border-radius:8px;overflow:hidden;">
                      <thead>
                        <tr style="background-color:#f9f9f9;border-bottom:1px solid #e4e4e7;">
                          <th style="padding:8px 8px;text-align:left;font-size:11px;font-weight:600;color:#52525b;text-transform:uppercase;letter-spacing:0.4px;">Ref</th>
                          <th style="padding:8px 8px;text-align:left;font-size:11px;font-weight:600;color:#52525b;text-transform:uppercase;letter-spacing:0.4px;">Title</th>
                          <th style="padding:8px 8px;text-align:left;font-size:11px;font-weight:600;color:#52525b;text-transform:uppercase;letter-spacing:0.4px;">Status</th>
                          <th style="padding:8px 8px;text-align:left;font-size:11px;font-weight:600;color:#52525b;text-transform:uppercase;letter-spacing:0.4px;">Age</th>
                          <th style="padding:8px 8px;text-align:left;font-size:11px;font-weight:600;color:#52525b;text-transform:uppercase;letter-spacing:0.4px;">Summary</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${tableRows}
                      </tbody>
                    </table>
                  </td>
                </tr>

                <!-- CTA -->
                <tr>
                  <td align="center" style="padding-bottom:28px;">
                    <a href="${inboxUrl}"
                       style="display:inline-block;background-color:${BRAND_COLOR};color:#ffffff;text-decoration:none;font-size:15px;font-weight:600;padding:14px 32px;border-radius:8px;"
                       onmouseover="this.style.backgroundColor='${BRAND_COLOR_DARK}'"
                       onmouseout="this.style.backgroundColor='${BRAND_COLOR}'">
                      View inbox
                    </a>
                  </td>
                </tr>

                <!-- Fallback link -->
                <tr>
                  <td style="padding-bottom:24px;border-top:1px solid #f4f4f5;padding-top:20px;">
                    <p style="margin:0;font-size:12px;color:#71717a;">Or paste this link into your browser:</p>
                    <p style="margin:6px 0 0;font-size:11px;word-break:break-all;">
                      <a href="${inboxUrl}" style="color:${BRAND_COLOR};text-decoration:none;">${inboxUrl}</a>
                    </p>
                  </td>
                </tr>

              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td align="center" style="padding-top:24px;">
              <p style="margin:0;font-size:12px;color:#a1a1aa;">&copy; ${new Date().getFullYear()} Quality Intelligence &middot; <a href="${APP_URL}" style="color:#a1a1aa;text-decoration:none;">quality-intelligence.co</a></p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`

  const text = `${count} ticket${count === 1 ? '' : 's'} need${count === 1 ? 's' : ''} your attention\n\n${textRows}\n\nView your inbox:\n${inboxUrl}\n\n— ${APP_NAME} / quality-intelligence.co`

  return { subject, html, text }
}
